import { ReactNode } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useAuthContext } from '@lp/core/auth';
import { PermissionCheckMode } from '@lp/core/routing/guards/types';
import { PermissionType } from '@/permissions';

export type PermissionGuardProps = {
  enabled: boolean;
  permissions?: PermissionType[];
  mode?: PermissionCheckMode;
  redirectTo?: string;
  children?: ReactNode;
  fallback?: ReactNode;
};

const DefaultFallback = () => (
  <div className="flex flex-col h-screen w-screen items-center justify-center bg-base-80">
    <h1 className="text-hxl">404</h1>
    <span className="text-l-sb text-base-20">Page Not Found</span>
  </div>
);

export const PermissionGuard = ({
  enabled,
  permissions = [],
  mode = 'some',
  redirectTo,
  children,
  fallback = <DefaultFallback />,
}: PermissionGuardProps) => {
  const { checkPermission } = useAuthContext();

  const hasPermission = mode === 'some' ? permissions.some(checkPermission) : permissions.every(checkPermission);

  if (!hasPermission && enabled) {
    if (redirectTo) {
      return <Navigate to={redirectTo} replace />;
    }
    return fallback;
  }

  return children ?? <Outlet />;
};
